import React, { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import axios from "axios";
import { useTranslation } from "react-i18next";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.DETAIL.ATTACHMENTS": "Attachments",
  "CONTACT_US_CENTER.DETAIL.DOWNLOAD": "Download",
  "CONTACT_US_CENTER.DETAIL.DOWNLOAD_ALL": "Download all",
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/** Force-download a single attachment. Fetches the file client-side and saves it via
 *  file-saver (same as "download all") so the browser saves directly instead of opening
 *  a throwaway tab. Falls back to a transient anchor if the blob fetch fails (e.g. CORS). */
const triggerDownload = async (att) => {
  if (!att.fileUrl) return;
  try {
    const res = await axios.get(att.fileUrl, { responseType: "blob" });
    saveAs(res.data, att.fileName || "file");
  } catch {
    const a = document.createElement("a");
    a.href = att.fileUrl;
    a.download = att.fileName || "";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

/** Build a filesystem-safe archive name from the post/question title. */
const buildZipName = (title) => {
  const base = (title || "attachments").trim().replace(/[\\/:*?"<>|]+/g, "_").slice(0, 60);
  return `${base || "attachments"}.zip`;
};

/**
 * Attachment list with per-file "Download" and (when >1) "Download all" (client-side .zip).
 * Shared by the board post detail page and the FAQ answer so both download the same way.
 * Renders nothing when there are no attachments.
 */
const AttachmentDownloads = ({ attachments = [], title }) => {
  const [zipping, setZipping] = useState(false);
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  if (!attachments.length) return null;

  // Stagger so the browser doesn't drop concurrent downloads.
  const downloadEachSeparately = () => {
    attachments.forEach((a, i) => setTimeout(() => triggerDownload(a), i * 300));
  };

  // Fetch every attachment client-side and bundle them into a single .zip (no server call).
  // Falls back to individual downloads if a file can't be fetched (e.g. cross-origin/CORS).
  const downloadAll = async () => {
    if (zipping) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const used = new Set();
      for (const att of attachments) {
        if (!att.fileUrl) continue;
        const res = await axios.get(att.fileUrl, { responseType: "blob" });
        // Disambiguate attachments that share a file name inside the archive.
        let name = att.fileName || "file";
        if (used.has(name)) {
          const dot = name.lastIndexOf(".");
          const stem = dot > 0 ? name.slice(0, dot) : name;
          const ext = dot > 0 ? name.slice(dot) : "";
          let i = 1;
          while (used.has(`${stem} (${i})${ext}`)) i++;
          name = `${stem} (${i})${ext}`;
        }
        used.add(name);
        zip.file(name, res.data);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, buildZipName(title));
    } catch {
      downloadEachSeparately();
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="cuc-detail-files">
      <div className="cuc-detail-files-head">
        <span className="cuc-detail-files-title">
          <span role="img" aria-label="attachment">📎</span> {t("CONTACT_US_CENTER.DETAIL.ATTACHMENTS")}
        </span>
        {attachments.length > 1 && (
          <button
            type="button"
            className="cuc-detail-download-all"
            onClick={downloadAll}
            disabled={zipping}
          >
            <i className={`pi ${zipping ? "pi-spin pi-spinner" : "pi-download"}`} />
            <span>{t("CONTACT_US_CENTER.DETAIL.DOWNLOAD_ALL")}</span>
          </button>
        )}
      </div>
      <ul>
        {attachments.map((a) => (
          <li key={a.id} className="cuc-detail-file-row">
            <span className="cuc-detail-file-name">
              <i className="pi pi-file cuc-detail-file-icon" />
              <span>{a.fileName} {a.fileSize ? `(${formatBytes(a.fileSize)})` : ""}</span>
            </span>
            <button
              type="button"
              className="cuc-detail-download"
              onClick={() => triggerDownload(a)}
            >
              <i className="pi pi-download" />
              <span>{t("CONTACT_US_CENTER.DETAIL.DOWNLOAD")}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AttachmentDownloads;
